/**
 * Competency Synchronization Utilities
 * 
 * This module provides utilities to ensure frontend and backend competency tracking
 * remains synchronized during interviews.
 */

export interface CompetencyMapping {
  frontendIndex: number
  frontendName: string
  backendName: string
  isValidMapping: boolean
}

export interface CompetencyValidationResult {
  correctedIndex: number
  correctedName: string
  correctedQuestions: number
  wasCorreted: boolean
  corrections: string[]
}

export interface InterviewEndDetection {
  shouldEnd: boolean
  reason: string
}

/**
 * Maps backend competency names to frontend competency indices
 * Handles mismatches between backend enum values and frontend competency definitions
 */
export function mapBackendCompetencyToFrontend(
  backendCompetencyName: string,
  frontendCompetencies: Array<{ name: string; displayName: string }>
): CompetencyMapping {
  
  // Standardized competency mapping - frontend and backend MUST match exactly
  const standardizedCompetencyMapping = new Map([
    ['Cultural Fit', 0],
    ['Technical Skills', 1], 
    ['Problem Solving', 2],
    ['Communication', 3],
    ['Relevance to Job Role', 4],
    ['Success Prediction Score', 5],
    ['Turnover Risk', 6],
    ['Proactivity Score', 7],
    ['Learning Ability', 8],
    ['Skill Match', 9],
    ['Work History Strength', 10],
    ['Leadership', 11]
  ])

  // Strategy 1: Direct mapping lookup
  if (standardizedCompetencyMapping.has(backendCompetencyName)) {
    const frontendIndex = standardizedCompetencyMapping.get(backendCompetencyName)!
    return {
      frontendIndex,
      frontendName: frontendCompetencies[frontendIndex]?.name || 'Unknown',
      backendName: backendCompetencyName,
      isValidMapping: true
    }
  }

  // Strategy 2: Fuzzy matching for partial names
  const normalizeText = (text: string) => 
    text.toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, ' ').trim()
  
  const normalizedBackend = normalizeText(backendCompetencyName)
  
  const fuzzyMatchIndex = frontendCompetencies.findIndex(comp => {
    const normalizedName = normalizeText(comp.name)
    const normalizedDisplay = normalizeText(comp.displayName)
    
    // Exact normalized match
    if (normalizedName === normalizedBackend || normalizedDisplay === normalizedBackend) {
      return true
    }
    
    // Key word matching
    const backendWords = normalizedBackend.split(' ').filter(w => w.length > 2)
    const nameWords = normalizedName.split(' ').filter(w => w.length > 2)
    
    if (backendWords.length > 0 && nameWords.length > 0) {
      return backendWords.some(backendWord => 
        nameWords.some(nameWord => 
          backendWord.includes(nameWord) || nameWord.includes(backendWord)
        )
      )
    }
    
    return false
  })

  if (fuzzyMatchIndex !== -1) {
    return {
      frontendIndex: fuzzyMatchIndex,
      frontendName: frontendCompetencies[fuzzyMatchIndex].name,
      backendName: backendCompetencyName,
      isValidMapping: true
    }
  }

  // Strategy 3: No match found - return invalid mapping
  return {
    frontendIndex: -1,
    frontendName: 'Unknown',
    backendName: backendCompetencyName,
    isValidMapping: false
  }
}

/**
 * Validates that a competency transition is valid (forward progression only)
 */
export function validateCompetencyTransition(
  fromIndex: number,
  toIndex: number,
  totalCompetencies: number,
  competencyAction: string
): { isValid: boolean; reason: string } {
  
  // Validate indices are within bounds
  if (fromIndex < 0 || fromIndex >= totalCompetencies || 
      toIndex < 0 || toIndex >= totalCompetencies) {
    return {
      isValid: false,
      reason: `Index out of bounds: from=${fromIndex}, to=${toIndex}, total=${totalCompetencies}`
    }
  }

  // Staying in same competency is always valid
  if (fromIndex === toIndex) {
    return { isValid: true, reason: 'Staying in same competency' }
  }

  // Forward progression is valid
  if (toIndex > fromIndex && competencyAction === 'transition') {
    return { isValid: true, reason: 'Valid forward progression' }
  }

  // Backward movement is not allowed
  if (toIndex < fromIndex) {
    return {
      isValid: false,
      reason: `Backward transition not allowed: ${fromIndex} -> ${toIndex}`
    }
  }

  // Forward movement without transition action
  if (toIndex > fromIndex && competencyAction !== 'transition') {
    return {
      isValid: false,
      reason: `Forward movement requires transition action, got: ${competencyAction}`
    }
  }

  return {
    isValid: false,
    reason: `Unexpected transition scenario: ${fromIndex} -> ${toIndex}, action: ${competencyAction}`
  }
}

/**
 * Calculates question count for a competency transition
 */
export function calculateQuestionCount(
  isTransitioning: boolean,
  currentQuestionCount: number,
  maxQuestions: number
): { newCount: number; isExtended: boolean; warning?: string } {
  
  if (isTransitioning) {
    // New competency starts at 1
    return { 
      newCount: 1, 
      isExtended: false 
    }
  } else {
    // Same competency, increment count
    const newCount = currentQuestionCount + 1
    const isExtended = newCount > 3 // Standard is 3 questions per competency
    
    let warning: string | undefined
    if (newCount > maxQuestions) {
      warning = `Question count ${newCount} exceeds maximum ${maxQuestions}`
    } else if (isExtended) {
      warning = `Extended questioning: ${newCount} questions (standard is 3)`
    }
    
    return { 
      newCount, 
      isExtended, 
      warning 
    }
  }
}

/**
 * Debug helper to log competency synchronization details
 */
export function logCompetencySync(
  aiCompetencyName: string,
  mapping: CompetencyMapping,
  transition: { fromIndex: number; toIndex: number; action: string },
  questionCount: { old: number; new: number },
  additionalInfo?: { [key: string]: any }
) {
  console.group('🔄 COMPETENCY SYNC DEBUG')
  console.log(`AI Reported: "${aiCompetencyName}"`)
  console.log(`Mapping: ${mapping.isValidMapping ? '✅' : '❌'} "${mapping.backendName}" -> [${mapping.frontendIndex}] "${mapping.frontendName}"`)
  console.log(`Transition: [${transition.fromIndex}] -> [${transition.toIndex}] (${transition.action})`)
  console.log(`Questions: ${questionCount.old} -> ${questionCount.new}`)
  
  if (additionalInfo) {
    Object.entries(additionalInfo).forEach(([key, value]) => {
      console.log(`${key}: ${JSON.stringify(value)}`)
    })
  }
  
  console.groupEnd()
}

/**
 * Validates and corrects competency state to prevent desync issues
 */
export function validateAndCorrectCompetencyState(
  aiResponse: any,
  currentState: {
    competencyIndex: number
    competencyName: string
    questionsAsked: number
  },
  competencies: Array<{ name: string; displayName: string }>
): {
  correctedIndex: number
  correctedName: string
  correctedQuestions: number
  wasCorreted: boolean
  corrections: string[]
} {
  const corrections: string[] = []
  let correctedIndex = currentState.competencyIndex
  let correctedName = currentState.competencyName
  let correctedQuestions = currentState.questionsAsked
  let wasCorreted = false

  // Map AI competency to frontend competency
  const mapping = mapBackendCompetencyToFrontend(
    aiResponse.current_competency || currentState.competencyName,
    competencies
  )

  if (!mapping.isValidMapping) {
    corrections.push(`Invalid AI competency: "${aiResponse.current_competency}"`)
    wasCorreted = true
  } else {
    const targetIndex = mapping.frontendIndex
    const targetName = mapping.frontendName

    // Validate transition is valid (no backwards movement, no skipping)
    if (targetIndex < currentState.competencyIndex) {
      corrections.push(`Blocked backwards movement: ${currentState.competencyIndex} -> ${targetIndex}`)
      wasCorreted = true
    } else if (targetIndex > currentState.competencyIndex + 1) {
      corrections.push(`Blocked competency skip: ${currentState.competencyIndex} -> ${targetIndex}`)
      // Force to immediate next competency
      if (currentState.competencyIndex + 1 < competencies.length) {
        correctedIndex = currentState.competencyIndex + 1
        correctedName = competencies[correctedIndex].name
        correctedQuestions = 1 // Reset for new competency
        corrections.push(`Enforced sequential progression to: ${correctedName}`)
      }
      wasCorreted = true
    } else if (targetIndex === currentState.competencyIndex + 1) {
      // Valid transition to next competency
      correctedIndex = targetIndex
      correctedName = targetName
      correctedQuestions = 1 // Reset for new competency
    } else if (targetIndex === currentState.competencyIndex) {
      // Staying in same competency
      correctedQuestions = Math.max(1, currentState.questionsAsked + 1)
    }
  }

  // Additional validation for question count
  if (correctedQuestions < 1) {
    correctedQuestions = 1
    corrections.push('Corrected negative question count to 1')
    wasCorreted = true
  }

  // More lenient cap for extended assessments
  if (correctedQuestions > 8) {
    correctedQuestions = Math.min(correctedQuestions, 8)
    corrections.push(`Capped excessive question count to ${correctedQuestions}`)
    wasCorreted = true
  }

  return {
    correctedIndex,
    correctedName,
    correctedQuestions,
    wasCorreted,
    corrections
  }
}

/**
 * Detects if an AI response indicates interview end through various methods
 */
export function detectInterviewEnd(
  aiResponse: any,
  currentCompetencyIndex: number,
  totalCompetencies: number
): { shouldEnd: boolean; reason: string } {
  console.log('🔍 INTERVIEW END DETECTION:', {
    end_interview: aiResponse.end_interview,
    currentCompetencyIndex,
    totalCompetencies,
    competency_action: aiResponse.competency_action,
    response_preview: typeof aiResponse.next_response === 'string' 
      ? aiResponse.next_response.substring(0, 100) + '...' 
      : aiResponse.next_response
  })

  const responseTextForGuards = (aiResponse.next_response || '').toLowerCase()
  const isSalaryExpectationQuestion =
    responseTextForGuards.includes('salary expectation') ||
    (responseTextForGuards.includes('salary') &&
      (/\?/.test(aiResponse.next_response || '') ||
        responseTextForGuards.includes('compensation') ||
        responseTextForGuards.includes('expect')))

  // Do not treat the salary-expectation closing question as interview completion
  if (isSalaryExpectationQuestion && aiResponse.end_interview !== 'yes') {
    console.log('🟢 INTERVIEW CONTINUES - Salary expectation question in progress')
    return { shouldEnd: false, reason: 'Salary expectation question — waiting for candidate answer' }
  }

  // Method 1: Direct end_interview field (most reliable)
  if (aiResponse.end_interview === 'yes') {
    return { shouldEnd: true, reason: 'AI explicitly set end_interview to yes' }
  }

  // Method 2: Competency action indicates completion
  if (aiResponse.competency_action === 'complete') {
    if (isSalaryExpectationQuestion) {
      return { shouldEnd: false, reason: 'Salary expectation question overrides complete action' }
    }
    return { shouldEnd: true, reason: 'AI set competency_action to complete' }
  }

  // Method 3: JSON patterns in response text indicating end
  if (typeof aiResponse.next_response === 'string') {
    const responseText = aiResponse.next_response.toLowerCase()
    
    // Look for end_interview patterns in JSON
    const jsonEndPatterns = [
      /"end_interview"\s*:\s*["']?yes["']?/i,
      /"end_interview"\s*:\s*true/i,
      /\{[^}]*"end_interview"[^}]*"yes"[^}]*\}/i
    ]
    
    for (const pattern of jsonEndPatterns) {
      if (pattern.test(aiResponse.next_response)) {
        return { shouldEnd: true, reason: 'Found end_interview signal in response JSON' }
      }
    }
  }

  // Method 4: At final competency (Leadership) with specific completion signals
  if (currentCompetencyIndex >= totalCompetencies - 1) {
    const responseText = (aiResponse.next_response || '').toLowerCase()
    
    // Strong completion indicators
    const strongCompletionPhrases = [
      /interview.*(?:complet|conclud|finish|end)/,
      /(?:complet|conclud|finish|end).*interview/,
      /thank.*(?:for|you).*(?:participat|time|interview)/,
      /(?:participat|time|interview).*thank/,
      /final.*question/,
      /concluding.*(?:interview|assessment)/,
      /assessment.*(?:complet|finish)/
    ]
    
    const hasStrongCompletion = strongCompletionPhrases.some(pattern => 
      pattern.test(responseText)
    )
    
    if (hasStrongCompletion) {
      return { shouldEnd: true, reason: 'At final competency with strong completion language' }
    }
    
    // Weak completion indicators (only trigger if we're really at the end)
    // Do NOT end if the response clearly asks a follow-up question (e.g. "That's excellent... Can you describe...?")
    const hasFollowUpQuestion =
      /[?]/.test(aiResponse.next_response || '') ||
      /(?:can you|could you|would you|tell me|describe|explain)\b/i.test(responseText)
    if (hasFollowUpQuestion) {
      // Praise + question = interview continues; skip weak completion
    } else {
      const weakCompletionPhrases = [
        /(?:well done|good job|excellent)/,
        /that.*(?:concludes|wraps up)/,
        /we.*(?:covered|discussed).*all/
      ]
      const hasWeakCompletion = weakCompletionPhrases.some(pattern =>
        pattern.test(responseText)
      )
      if (hasWeakCompletion && currentCompetencyIndex === totalCompetencies - 1) {
        return { shouldEnd: true, reason: 'At final competency with weak completion indicators' }
      }
    }
  }

  // Method 5: No question in response at final stages (AI forgot to continue)
  if (currentCompetencyIndex >= totalCompetencies - 2) { // Last 2 competencies
    const responseText = aiResponse.next_response || ''
    const hasQuestion = /[?]/.test(responseText) || 
                       /(?:can you|could you|would you|tell me|describe|explain|what|how|why|when|where)\b/i.test(responseText)
    
    if (!hasQuestion && responseText.length > 20) {
      return { shouldEnd: true, reason: 'No question found in response at final stages - likely completion' }
    }
  }

  console.log('🟢 INTERVIEW CONTINUES - No end conditions detected')
  return { shouldEnd: false, reason: 'No end conditions detected' }
}